import axios from "axios";
import { toast } from "react-toastify";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let refreshSubscribers: any = [];

const onRefreshed = (token: any) => {
  refreshSubscribers.map((callback: any) => callback(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback: any) => {
  refreshSubscribers.push(callback);
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (!user.refreshToken) {
        localStorage.clear();
        window.location.href = "/";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((token: any) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = user.refreshToken;
        const userId = user?.user?.id;

        const response: any = await axios.post(
          `${process.env.REACT_APP_SERVER_URL?.replace(/\/$/, '')}/v1/auth/refresh_token`,
          {
            oldRefreshToken: refreshToken,
            userId,
          }
        );

        localStorage.setItem("user", JSON.stringify(response?.data?.data));

        isRefreshing = false;
        const newAccessToken = response?.data?.data?.access_token;
        onRefreshed(newAccessToken);

        // Dynamically import Redux store and update tokens
        import("../redux/app/store").then(({ store }) => {
          import("../redux/features/authSlice").then(({ updateTokens }) => {
            store.dispatch(updateTokens({
              access_token: response?.data?.data?.access_token,
              refreshToken: response?.data?.data?.refreshToken
            }));
          });
        });

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        toast.error("Refresh token expired or invalid. Redirecting to login.");
        localStorage.clear();
        window.location.href = "/";
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
